import { useEffect, useRef, useState } from "react";
import { Send, X, Minimize2 } from "lucide-react";
import { sendAssistantChat } from "../services/api";

const SESSION_FLAG = "assistant_autopen_seen";
const SUGGESTED_PROMPTS = [
  "What is ApplicantHQ?",
  "What does this page do?",
  "How is my resume?",
];
const MAX_CONTEXT_TURNS = 6;

const MascotAssistant = ({ isVisible, currentScreen }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hi, I can help with ApplicantHQ questions and career-focused encouragement.",
      restricted: false,
    },
  ]);
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef(null);

  const canShow = isVisible;

  useEffect(() => {
    if (!canShow) return;

    const seen = sessionStorage.getItem(SESSION_FLAG);
    if (!seen) {
      setIsOpen(true);
      sessionStorage.setItem(SESSION_FLAG, "1");
    }
  }, [canShow]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
    });
  }, [messages, isSending, isOpen]);

  const sendMessage = async (message) => {
    const trimmed = (message || "").trim();
    if (!trimmed || isSending) return;
    setIsSending(true);
    const context = messages.slice(-MAX_CONTEXT_TURNS).map((msg) => ({
      role: msg.role,
      text: msg.text,
      restricted: !!msg.restricted,
    }));
    setMessages((prev) => [...prev, { role: "user", text: trimmed }]);
    setDraft("");
    try {
      const result = await sendAssistantChat(trimmed, currentScreen, context);
      const reply =
        result?.reply ||
        "I can help with ApplicantHQ guidance and career-focused encouragement.";
      const restricted = !!result?.restricted;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: reply, restricted },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          text: "I ran into an issue. Please try again.",
          restricted: false,
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleSuggestion = (text) => {
    setDraft(text);
    sendMessage(text);
  };

  if (!canShow) return null;

  return (
    <div className="assistant-root">
      <button
        className="assistant-launcher"
        onClick={() => setIsOpen((v) => !v)}
      >
        <img src="/assets/mascot_casual.png" alt="Applicant mascot" />
      </button>
      {isOpen && (
        <div className="assistant-panel">
          <div className="assistant-header">
            <strong>ApplicantHQ Assistant</strong>
            <div className="assistant-header-actions">
              <button
                className="assistant-icon-btn"
                onClick={() => setIsOpen(false)}
              >
                <Minimize2 size={16} />
              </button>
              <button
                className="assistant-icon-btn"
                onClick={() => setIsOpen(false)}
              >
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="assistant-messages">
            {messages.map((m, idx) => (
              <div
                key={`${m.role}-${idx}`}
                className={`assistant-msg ${m.role} ${m.restricted ? "restricted" : ""}`}
              >
                {m.text}
              </div>
            ))}
            {isSending && (
              <div className="assistant-msg assistant assistant-typing">
                <span></span>
                <span></span>
                <span></span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="assistant-text-compose">
            <div className="assistant-suggestions">
              {SUGGESTED_PROMPTS.map((prompt) => (
                <button
                  key={prompt}
                  type="button"
                  disabled={isSending}
                  onClick={() => handleSuggestion(prompt)}
                >
                  {prompt}
                </button>
              ))}
            </div>
            <div className="assistant-input-row">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(draft);
                  }
                }}
                placeholder="Ask about ApplicantHQ or anything career-adjacent..."
              />
              <button
                disabled={isSending}
                onClick={() => sendMessage(draft)}
                className="assistant-send-btn"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MascotAssistant;
